import { useTranslations } from '@vocab/react';
import { Alert, Text, TextLink } from 'braid-design-system';
import { useCallback, useEffect } from 'react';

import { RESUME } from 'src/components/Router';
import { Link } from 'src/components/Router/Link';
import { EDIT } from 'src/consts';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import type { AnalyticsNudgeType } from 'src/modules/analytics/types';
import type { VisibilityLevel } from 'src/types/graphql';

import { useGetProfileVisibility } from '../ProfileVisibilityV2/data';

import translations from './.vocab';

export const DefaultResumeNudge = () => {
  const { t } = useTranslations(translations);

  const { profileVisibilityLevel } = useGetProfileVisibility();

  const alertMessage =
    profileVisibilityLevel === 'Standard'
      ? t(
          'Your resumé is not currently visible to employers because you have not selected a default resumé.',
        )
      : t('Currently you don’t have a resumé selected as default.');

  const { nudgeTracker } = useAnalytics();

  const visibilityToNudgeTypeMap: Record<VisibilityLevel, AnalyticsNudgeType> =
    {
      Standard: 'default_resume_standard_visibility',
      Limited: 'default_resume_limited_visibility',
      Hidden: 'default_resume_hidden_visibility',
    };

  const nudgeType =
    visibilityToNudgeTypeMap[profileVisibilityLevel || 'Hidden'];

  useEffect(() => {
    nudgeTracker({
      eventName: 'nudge_viewed',
      nudgeType,
      callToAction: 'set_default_resume',
      callToActionType: 'nudge',
    });
  }, [nudgeTracker, nudgeType]);

  const onClick = useCallback(() => {
    nudgeTracker({
      eventName: 'nudge_pressed',
      nudgeType,
      callToAction: `set_default_resume`,
      callToActionType: 'nudge',
    });
  }, [nudgeTracker, nudgeType]);

  return (
    <Alert tone="info" data={{ automation: 'default-resume-nudge' }}>
      <Text>
        {alertMessage}{' '}
        <Link
          to={RESUME}
          mode={EDIT}
          component={TextLink}
          data={{ automation: 'default-resume-nudge-edit-link' }}
          onClick={onClick}
        >
          {t('Select default resumé')}
        </Link>
      </Text>
    </Alert>
  );
};
